<template>
    <div
        v-if="!isLoading"
        v-spinner="loading"
        class="web-tab-detail"
    >
        <v-spinner
            :offset-y="300"
            size="large"
        />
        <web-tab-detail-header
            :is-create="isCreate"
            :is-default-tpl="isDefaultTpl"
        />
        <web-tab-detail-wpa-form
            v-if="!isDefaultTpl"
            :wpa-form-data="wpaFormData"
            :hasMigrated="hasMigrated"
            @getNowData="getNowData"
            @setErrors="setErrors"
        />
        <div class="page-content__webim-style-select page-section ">
            <webim-style-select
                ref="webImStyleRef"
                :is-default-tpl="isDefaultTpl"
                :style-select-data="styleSelectData"
                :hasAdaptThemeColorPrivilege="hasAdaptThemeColorPrivilege"
                @getNowData="getNowData"
                @getThemBgData="getThemBgData"
            />
        </div>
        <div class="page-section">
            <webim-external-select
                ref="webImExternalRef"
                :external-data="webImExternalData"
                :channel-ids="channelIds"
                :disable-kfuin="webimDisableKfuin"
                @getWebImExternalData="getWebImExternalData"
                @getNowData="getNowData"
                @setErrors="setErrors"
            />
        </div>
        <div class="page-section page-section--footer">
            <customer-connect
                ref="customerConnectRef"
                :connect-data="connectInfoData"
                :channel-ids="channelIds"
                :is-default-tpl="isDefaultTpl"
                :enable-capture="enableCapture"
                :channel-type="channelType"
                :hasMigrated="hasMigrated"
                :hasAdaptThemeColorPrivilege="hasAdaptThemeColorPrivilege"
                @getNowData="getNowData"
                @getConnectInfoData="getConnectInfoData"
            />
        </div>
        <div class="webim-style-select__action">
            <v-button
                :disabled="disabled"
                :loading="submiting"
                @click="saveAndPublish"
                status="primary"
                class="save-btn"
            >
                保存模板
            </v-button>
            <v-button
                :loading="submiting"
                @click="initData"
                class="cancel-btn"
            >
                取消
            </v-button>
        </div>
    </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import WebimStyleSelect from '../webim-style-select/index';
import { formatStyleData, convertStyleDataForSubmit, isSameObj, authorizeTipDefault } from './config';
import WebimExternalSelect from './webim-external-select/index';
import WebTabDetailHeader from './web-tab-header/Index.vue';
import WebTabDetailWpaForm from './wpa-detail-form/Index.vue';
import CustomerConnect from './customer-connect';
import { isSessionWindowStyleMigrate } from '@/cgi/getWhiteList';
import Gateway from '@/utils/Gateway';

import { Icon } from 'vhtml-ui';
const sprites = require.context('@/assets/icons', true, /[^.]+\.svg$/);
Icon.addSprites(sprites);


// 新建模板id初始为0
const NEW_TPL_ID = 0;

const LEAVE_CONFIRM_MSG = '您的编辑可能未保存，是否离开';

export default {
    components: {
        WebTabDetailHeader,
        WebTabDetailWpaForm,
        WebimStyleSelect,
        WebimExternalSelect,
        CustomerConnect,
    },
    inject: ['context'],
    provide() {
        return {
            channelType: this.channelType,
        };
    },
    props: {
        isDefaultTpl: {
            type: Boolean,
            default: false,
        },
        tplId: {
            type: Number,
            default: NEW_TPL_ID,
        },
        channelType: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            isLoading: true,
            // 加载数据
            loading: true,
            // 是否正在提交
            submiting: false,

            // 存储的默认数据
            cacheData: {},
            // 当前的数据
            nowData: {},

            // 模板名称和wpa应用位置表单数据
            wpaFormData: {},
            // 选择主题和背景色的数据
            styleSelectData: {},
            // 形象与客户引流数据
            webImExternalData: {},
            // 是否是白名单用户，展示截图; 1:展示, 0:不展示
            enableCapture: 0,
            // 是否提示联系经销商开通
            webimDisableKfuin: window.pageData.disableKfuin,
            // 客户沟通数据
            connectInfoData: {},

            // 表单校验
            errors: {
                wpaFormData: false,
                externalData: false,
            },
            hasMigrated: false, // 会话互动导航是否已经迁移
            hasAdaptThemeColorPrivilege: false, // 是否有气泡导航适配主题色权限
        };
    },
    computed: {
        channelIds() {
            return this.context.channelIds;
        },
        isCreate() {
            return this.tplId === NEW_TPL_ID;
        },
        disabled() {
            const hasError = Object.values(this.errors).some(item => item);
            const hasChange = !isSameObj(this.nowData, this.cacheData);
            const hasTemplateName = (this.nowData.templateName || '').trim();
            const topTypeIsCustomButNoCustomName = this.nowData.linkInfo
                && this.nowData.linkInfo.topType === 3
                && !(this.nowData.linkInfo.customTopName || '').trim();
            const openNameIsCustomButNoCustomNameOrAvatar = this.nowData.linkInfo
                && this.nowData.linkInfo.openName === 3
                && (!(this.nowData.linkInfo.customOpenName || '').trim() || !this.nowData.linkInfo.customOpenAvatar);
            const externalMixTypeAndSidebarID = this.nowData.linkInfo
                && this.nowData.linkInfo.externalType === 3
                && (!this.nowData.linkInfo.externalMixType || +this.nowData.linkInfo.sidebarID === 0);

            return [
                hasError,
                !hasChange,
                !hasTemplateName,
                topTypeIsCustomButNoCustomName,
                openNameIsCustomButNoCustomNameOrAvatar,
                externalMixTypeAndSidebarID,
            ].some(Boolean);
        },
    },
    created() {
        isSessionWindowStyleMigrate()
            .then((val) => {
                this.hasMigrated = val;
            })
            .catch((e) => {
                console.log('获取会话互动导航迁移状态失败', e);
            })
            .finally(() => {
                this.isLoading = false;
            });
    },
    mounted() {
        this.fetchData();
    },
    methods: {
        fetchData() {
            // 新创建的模板初始化通过id: 0的参数去拉取默认配置
            const params = {
                id: this.tplId,
                channelType: this.channelType === 1 ? 54 : 4,
            };

            this.loading = true;

            // 获取会话窗口样式配置
            const getWebWindowStyle = this.$ajax.gatewayGet('/v1/kf/receptionConf/getWebWindowStyle', { params })
                .then((res) => {
                    if (+res.code === 0) {
                        // 存储一份数据
                        this.cacheData = cloneDeep(formatStyleData(res.data));
                        this.nowData = cloneDeep(formatStyleData(res.data));
                        this.initData();
                    } else {
                        this.$message.error(res.msg || '发生错误，请刷新重试');
                    }
                })
                .catch((err) => {
                    this.$message.error(err.msg || '发生错误，请刷新重试');
                });

            // 权限检查
            const checkPrivilege = Gateway.post('/receptionConf/privilege/check', {
                id: [759], // 权限ID列表  759-网页窗口样式高级设置 给资生堂做的高级UI颜色配置
                logic: 1, // 关系 1-and 2-or（默认为2） 当权限ID存在多个时生效
                source: 1, // 1-账户中心权限 2-客户端权限
            })
                .then((data) => {
                    this.hasAdaptThemeColorPrivilege = data.ok;
                })
                .catch((err) => {
                    throw err;
                });

            Promise.all([getWebWindowStyle, checkPrivilege])
                .finally(() => {
                    this.loading = false;
                });
        },
        // --- 下方数据获取流程： self.saveAndPublish->sub.saveData->$emit->this.xxx=data; ---
        // 获取主题色的数据
        getThemBgData(data) {
            this.styleSelectData = cloneDeep(data);
        },
        // 获取对外展示样式数据
        getWebImExternalData(data) {
            this.webImExternalData = cloneDeep(data);
        },
        // 获取客户引流数据
        getConnectInfoData(data) {
            this.connectInfoData = cloneDeep(data);
        },
        // 获取当前的数据
        getNowData(data, type) {
            this.nowData = {
                ...this.nowData,
                [type]: cloneDeep(data),
            };
        },
        // 设置当前表单是否存在错误
        setErrors(type, value) {
            this.errors[type] = value;
        },
        // 保存当前数据
        async saveAndPublish() {
            this.submiting = true;
            setTimeout(() => {
                // 防止后续流程错误卡死提交逻辑。
                this.submiting = false;
            }, 10 * 1000);

            // 验证气泡导航
            if (this.$refs.customerConnectRef
                && this.$refs.customerConnectRef.$refs.interactiveNavSetting)
            {
                const formOK = await this.$refs.customerConnectRef.$refs.interactiveNavSetting.validate();
                if (!formOK) {
                    this.submiting = false;
                    return;
                }
            }

            this.$refs.webImStyleRef.saveData();
            this.$refs.webImExternalRef.saveData();
            this.$refs.customerConnectRef && this.$refs.customerConnectRef.saveData();

            let paramsObj = cloneDeep(this.nowData);
            // 转换数据
            paramsObj = convertStyleDataForSubmit(paramsObj);

            if (paramsObj.linkInfo) {
                // 删除展示的字段
                delete paramsObj.linkInfo.companyAddress;
                delete paramsObj.linkInfo.companyEmail;
                delete paramsObj.linkInfo.companyIntro;
                delete paramsObj.linkInfo.companyLogo;
                delete paramsObj.linkInfo.companyName;
                delete paramsObj.linkInfo.companyPhone;
                delete paramsObj.linkInfo.companySignature;
                delete paramsObj.linkInfo.companySite;
                paramsObj.linkInfo.recommendation = paramsObj.linkInfo.recommendation
                    .map(item => ({ value: item.key, type: item.type }));

                // 顶部名称如果不是自定义的话，删除不需要的字段，避免带到服务端落库导致下次拉取渲染出之前编辑的结果
                if (paramsObj.linkInfo.topType !== 3) {
                    delete paramsObj.linkInfo.customTopName;
                }

                // 左侧对外名称如果不是自定义的话，删除不需要的字段，避免带到服务端落库导致下次拉取渲染出之前编辑的结果
                if (paramsObj.linkInfo.openName !== 3) {
                    delete paramsObj.linkInfo.customOpenName;
                    delete paramsObj.linkInfo.customOpenAvatar;
                }
            }
            if (paramsObj.guidanceInfo && paramsObj.guidanceInfo.guidanceData) {
                const newObj = {
                    type: paramsObj.guidanceInfo.guidanceData.type,
                    value: paramsObj.guidanceInfo.guidanceData.key,
                };
                paramsObj.guidanceInfo.guidanceData = { ...newObj };
            }
            // 后台层级不同，特殊处理
            paramsObj.isManualClose = paramsObj.guidanceInfo.isManualClose;
            // 内容授权提醒 默认值设置
            const { authorizeTip = {} } = paramsObj.guidanceInfo;
            if ((!authorizeTip || !authorizeTip.title) && paramsObj.guidanceInfo.isContentAuthorize) {
                paramsObj.guidanceInfo.authorizeTip = { ...authorizeTipDefault };
            }
            // 处理wpaFormData
            const originWpaApplys = this.cacheData.wpaApplys;
            const nowWpaApplys = this.nowData.wpaApplys;
            paramsObj.addWpaApplys = nowWpaApplys
                .filter(item => !originWpaApplys.find(({ wpaId }) => wpaId === item.wpaId));
            paramsObj.delWpaApplys = originWpaApplys
                .filter(item => !nowWpaApplys.find(({ wpaId }) => wpaId === item.wpaId));
            // 增加channel_type字段，区分小程序插件和网页
            paramsObj.channelType = this.channelType === 1 ? 54 : 4;
            // 删除数据上面的wpaApplys字段
            delete paramsObj.wpaApplys;

            // 新建的话要删除id
            if (this.isCreate) {
                delete paramsObj.id;
                if(paramsObj.navInfo && paramsObj.navInfo.navData) {
                    // 后端lokli要求 新建模板时，互动导航的提交数据不要id，并且name填空
                    // 否则会同时更新默认模板
                    delete paramsObj.navInfo.navData.id;
                    paramsObj.navInfo.navData.name = '';
                }
            }            

            const CREATE_URL = '/v1/kf/receptionConf/createWebWindowStyle';
            const UPDATE_URL = '/v1/kf/receptionConf/updateWebWindowStyle';

            this.$ajax
                .gatewayPost(this.isCreate ? CREATE_URL : UPDATE_URL, {
                    ...paramsObj,
                })
                .then((res) => {
                    this.submiting = false;

                    if (res.code !== 0) {
                        throw new Error(res.message || '保存失败');
                    }

                    // 由于后面直接跳回列表，其实这两行代码应该可以不需要了
                    this.cacheData = cloneDeep(this.nowData);
                    this.initData();

                    // 保存成功后返回
                    this.$message.success(`${this.isCreate ? '创建' : '保存'}成功并发布`);
                    const tab = this.channelType === 1 ? 'miniPlugin' : 'web';
                    this.$router.push({ name: 'ChatWindow', query: { tab }});
                })
                .catch((err) => {
                    this.submiting = false;
                    this.$message.error(err.message || '发生错误，请刷新重试');
                });
        },
        // 取消按钮
        initData() {
            // --- 数据处理逻辑：---
            // 下发 this.xxx 数据用来做组件编辑时的数据临时存储和传递
            // this.nowData 为提交数据。
            // 每次更新 this.xxxData 的时候，同时也要更新 this.nowData[type]。
            this.wpaFormData = cloneDeep({
                templateName: this.cacheData.templateName,
                originWpaApplys: this.cacheData.wpaApplys,
                nowWpaApplys: this.cacheData.wpaApplys,
            });
            this.styleSelectData = cloneDeep({
                styleConfig: this.cacheData.styleConfig,
                theme: this.cacheData.theme,
            });
            // 形象与引流数据
            this.webImExternalData = cloneDeep({
                linkInfo: this.cacheData.linkInfo,
                guidanceInfoForYinliu: this.cacheData.guidanceInfoForYinliu,
            });
            this.connectInfoData = cloneDeep({
                ...this.cacheData.guidanceInfoForHudong,
            });
            this.enableCapture = cloneDeep(this.cacheData.enableCapture);
            if(this.$refs.customerConnectRef && this.$refs.customerConnectRef.$refs.interactiveNavSetting) {
                if(this.connectInfoData.interactiveNavData && this.connectInfoData.interactiveNavData.enableSwitch) {
                    // 点取消后 如果气泡导航是开启状态 默认选中第一个菜单
                    // 否则会出现当前选中的第 N 个菜单被清除 右侧相关配置UI消失
                    this.$refs.customerConnectRef.currNavMenuIndex = 0;
                }
            }
        },
    },
};
</script>
<style lang="less">
.hidden-footer{
    display: none;
}
</style>
<style lang="less" scoped>
@import '~@/common/common';
.web-tab-detail {
    padding-bottom: 20px;

    // 新菜单隐藏了底部 qdAdminFooter 导致页面底边距不够 部分内容被fixed的底部按钮区域遮挡 因此增加底部边距
    .page-section {
        &--footer {
            padding-bottom: 30px;
        }
    }

    .webim-style-select__action {
        position: fixed;
        z-index: 10;
        bottom: 0;
        left: 0;
        right: 0;
        width: 100%;
        border-top: 1px solid rgba(18, 42, 79, 0.08);
        display: flex;
        padding: 16px 24px;
        justify-content: center;
        align-items: center;
        background-color: #fff;
        .save-btn {
            width: 150px;
            line-height: 45px;
            font-size: 16px;
            margin-right: 20px;
        }
        .cancel-btn {
            font-size: 16px;
            width: 150px;
            line-height: 45px;
        }
    }
}
</style>
